/**
 * Anzeigenamen der Akte-Ordner.
 *
 * Der Storage hält die Ordner bewusst ASCII-portabel: `02_Vertraege`, `05_Steuer-Behoerde`,
 * `Maerz`. Das ist eine Ablage-Konvention des `PropertyFilePathBuilder`, kein Produkttext —
 * die Ordner-Rail spricht deshalb längst „Verträge" und „Steuer & Behörde", während die
 * Inhaltsliste den rohen Schlüssel zeigte. Zwei Namenswelten für dieselbe Sache.
 *
 * Diese Datei ist die eine Übersetzung Speicher → Produktsprache. Sie ändert **nichts** an den
 * tatsächlichen Ordnernamen; jede Mutation läuft weiter über den echten Pfad.
 */
import { TOP_FOLDERS } from './akteFolderMeta';
import { segmentsOf } from './akteFolderRules';

/** Scaffold-Unterordner, deren Speichername kein guter Produkttext ist (voller Pfad → Text). */
const SUBFOLDER_NAMES: Readonly<Record<string, string>> = {
  '03_Rechnungen/Ausgehend': 'Ausgangsrechnungen',
  '05_Steuer-Behoerde/Stamm': 'Stammunterlagen',
  '08_Fotos/Zustand-Checkin': 'Zustand bei Check-in',
  '08_Fotos/Schaeden': 'Schäden',
};

/**
 * Die Monatsordner kommen aus `PropertyFilePathBuilder.MonthNames` und sind bis auf einen
 * bereits korrekt geschrieben. Bewusst kein generisches `ae→ä`: das würde auch Eigennamen in
 * selbst angelegten Ordnern umschreiben.
 */
const MONTH_NAMES: Readonly<Record<string, string>> = { Maerz: 'März' };

const TOP_TITLES = new Map(TOP_FOLDERS.map((f) => [f.folderPath, f.title]));

/** Der Wurzelknoten der Akte hat keinen Pfad — er heißt in der UI schlicht „Akte". */
export const AKTE_ROOT_LABEL = 'Akte';

/**
 * Anzeigename eines Ordners aus seinem vollen Pfad. Unbekannte (selbst angelegte) Ordner
 * behalten ihren Namen unverändert — der stammt vom Nutzer, nicht vom Speicher.
 */
export function folderDisplayName(path: string): string {
  if (!path) return AKTE_ROOT_LABEL;
  const top = TOP_TITLES.get(path);
  if (top) return top;
  const fixed = SUBFOLDER_NAMES[path];
  if (fixed) return fixed;
  const last = segmentsOf(path).at(-1) ?? path;
  return MONTH_NAMES[last] ?? last;
}

/** Voller Anzeigepfad ohne den Akte-Wurzelknoten: „Rechnungen / 2026 / März". */
export function folderDisplayPath(path: string): string {
  return displaySegments(path).join(' / ');
}

/**
 * Anzeigepfad ohne die Top-Kategorie — für Ziel-Picker, die ohnehin nur innerhalb einer
 * Kategorie anbieten: „2026 / März" statt „Rechnungen / 2026 / März".
 */
export function folderDisplayPathWithinCategory(path: string): string {
  return displaySegments(path).slice(1).join(' / ');
}

function displaySegments(path: string): string[] {
  let acc = '';
  return segmentsOf(path).map((seg) => {
    acc = acc ? `${acc}/${seg}` : seg;
    return folderDisplayName(acc);
  });
}
